import { Link } from 'react-router-dom';
import { FACILITATORS, ASSETS, CLOSING_STATEMENT } from '../lib/content';
import { User, Users, Heart, Home as HomeIcon, Check, ExternalLink, Mail } from 'lucide-react';

const SUPPORTS = [
  {
    icon: User,
    title: 'Individual Therapy',
    body:
      'A confidential therapeutic space to explore your relationship with food, body, emotions and self.',
    intro: 'Suitable for those experiencing:',
    items: [
      'Eating disorders',
      'Disordered eating',
      'Emotional eating',
      'Body image difficulties',
      'Perfectionism',
      'Anxiety',
      'Depression',
      'Trauma',
      'Life transitions',
      'Relationship difficulties',
      'Spiritual questions connected to recovery',
    ],
  },
  {
    icon: Heart,
    title: 'Support for Parents',
    body:
      'Supporting a loved one through an eating disorder can feel overwhelming.',
    long: [
      'Parents often experience uncertainty, fear, guilt and exhaustion while trying to provide the best possible care.',
      'These sessions offer guidance, understanding and practical support for parents navigating this journey.',
    ],
  },
  {
    icon: Users,
    title: 'Couples Therapy',
    body: 'Eating disorders affect relationships as well as individuals.',
    long: [
      'Couples therapy offers a compassionate space to strengthen communication, increase understanding and support recovery together.',
    ],
  },
  {
    icon: HomeIcon,
    title: 'Family Therapy',
    body: 'Recovery often takes place within relationships.',
    long: [
      'Family therapy can support healthier communication, understanding and connection while helping families navigate the challenges that eating disorders may bring.',
    ],
  },
];

const THERAPIST_PROFILES = {
  'Dr Charlotte Bamber': {
    photo: ASSETS.facilitatorCharlotte,
    tag: ['Medical Doctor', 'Psychotherapist', 'Anthroposophical Psychotherapist', 'Founder of Whole Health'],
    offers: [
      'Individual psychotherapy',
      'Eating disorder recovery',
      'Emotional eating',
      'Parents of individuals with eating disorders',
      'Couples therapy',
      'Trauma-informed psychotherapy',
      'Anthroposophically informed psychotherapy',
      'Spirituality and mental health',
    ],
    practice_url: 'https://www.drcharlottebamber.com',
    practice_label: "Visit Charlotte's Practice",
    email: 'aboutwholehealth@gmail.com',
  },
  'Jaquelynn A. Taipe': {
    photo: ASSETS.facilitatorJaquelynn,
    tag: ['Psychotherapist', 'Family Therapist', 'Anthroposophical Psychotherapist'],
    offers: [
      'Individual therapy',
      'Family therapy',
      'Eating disorder recovery',
      'Relationship support',
      'Therapeutic support for families',
    ],
    practice_url: 'https://therapeuticpaths.co.uk',
    practice_label: "Visit Jaquelynn's Practice",
    email: 'familytherapyuk@gmail.com',
  },
};

const ALONGSIDE = [
  'NHS services',
  'Private therapists',
  'Dietitians',
  'Psychiatrists',
  'General Practitioners',
  'Family members',
  'Other healthcare professionals',
];


export default function Therapy() {
  return (
    <div data-testid="page-therapy">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[340px]">
        <img src={ASSETS.sunThroughTrees} alt="Soft sunlight filtering through trees" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/30 via-brand-bg/30 to-brand-bg" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full text-center">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4 bg-brand-bone/80 inline-block px-4 py-1.5 rounded-full">Professional Support</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-brown leading-[1.05] max-w-4xl mx-auto">
              One-to-One Therapy <em className="italic text-brand-brown-light">&</em> Professional Support
            </h1>
          </div>
        </div>
      </section>

      {/* OPENING */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-brown text-center leading-tight">
            Every Recovery Journey Is Different
          </h2>
          <div className="mt-8 space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>Recovery from an eating disorder or a difficult relationship with food, body and self is deeply personal. No two journeys are the same.</p>
            <p>Some people feel nourished by the support of a therapeutic group. Others feel more comfortable beginning with individual therapy. Some may wish to involve their family or partner in the recovery process. Others may be seeking specialist support alongside existing treatment.</p>
            <p>At Nurture the Self, we recognise that different forms of support are needed by different people, at different stages of life and at different points in the recovery journey. Our intention is to offer flexible pathways that meet people where they are.</p>
            <p>Whether you are looking for individual psychotherapy, family support, couples therapy or guidance for parents, we are here to support you with compassion, clinical expertise and a whole-person approach.</p>
          </div>
        </div>
      </section>

      {/* WAYS WE CAN SUPPORT YOU */}
      <section className="py-16 bg-brand-bg-soft">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Ways we can support you</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown">Four forms of therapeutic care</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SUPPORTS.map((s, i) => (
              <div key={s.title} data-testid={`therapy-support-${i}`} className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5">
                <div className="h-12 w-12 rounded-full bg-brand-sage flex items-center justify-center mb-5">
                  <s.icon size={20} className="text-brand-brown" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-brand-brown leading-snug mb-3">{s.title}</h3>
                <p className="font-sans text-base text-brand-brown-light/95 leading-relaxed">{s.body}</p>
                {s.long && (
                  <div className="mt-4 space-y-3 font-sans text-base text-brand-brown-light/95 leading-relaxed">
                    {s.long.map((p, k) => <p key={k}>{p}</p>)}
                  </div>
                )}
                {s.items && (
                  <>
                    <p className="mt-5 font-sans text-sm text-brand-brown-muted">{s.intro}</p>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 font-sans text-sm text-brand-brown-light/95">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-2.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-brand-gold mt-2 shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET YOUR THERAPISTS */}
      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Meet your therapists</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown">Two experienced practitioners</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {FACILITATORS.map((f, i) => {
              const p = THERAPIST_PROFILES[f.name] || {};
              return (
                <article key={f.name} data-testid={`therapist-${i}`} className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5">
                  <div className="flex items-start gap-6 mb-6">
                    <img
                      src={p.photo}
                      alt={`Portrait of ${f.name}`}
                      className="w-24 h-24 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl text-brand-brown leading-tight">{f.name}</h3>
                      <p className="mt-2 font-sans text-xs uppercase tracking-[0.18em] text-brand-gold leading-relaxed">
                        {(p.tag || []).join(' · ')}
                      </p>
                    </div>
                  </div>

                  <p className="font-sans text-sm text-brand-brown-muted mb-3">Offers</p>
                  <ul className="space-y-2.5 mb-8">
                    {(p.offers || []).map((o) => (
                      <li key={o} className="flex items-start gap-3 font-sans text-sm text-brand-brown-light/95 leading-relaxed">
                        <Check size={16} className="text-brand-gold mt-0.5 shrink-0" strokeWidth={1.6} />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href={p.practice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`therapist-practice-${i}`}
                      className="inline-flex items-center gap-2 font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-6 py-2.5 hover:bg-brand-brown-light transition-colors duration-500"
                    >
                      {p.practice_label} <ExternalLink size={14} strokeWidth={1.6} />
                    </a>
                    <a
                      href={`mailto:${p.email}`}
                      data-testid={`therapist-email-${i}`}
                      className="inline-flex items-center gap-2 font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-6 py-2.5 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
                    >
                      <Mail size={14} strokeWidth={1.6} /> Email {f.name.split(' ')[0]}
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW INDIVIDUAL COMPLEMENTS */}
      <section className="py-20 bg-brand-bg-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-brown text-center leading-tight">
            How Individual Therapy Can Complement Nurture the Self
          </h2>
          <div className="mt-8 space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>Some individuals choose to undertake one-to-one therapy before joining a group. Others attend therapy alongside the programme. Some begin with Path I and later decide they would benefit from additional individual support.</p>
            <p>There is no single &ldquo;right&rdquo; way to recover.</p>
            <p>We are committed to offering flexible, compassionate support that honours each person&rsquo;s unique journey.</p>
          </div>
        </div>
      </section>

      {/* NOT READY FOR GROUP CALLOUT */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div data-testid="therapy-not-ready" className="relative overflow-hidden rounded-3xl bg-brand-sage p-10 md:p-14 border border-brand-brown/5">
            <div className="absolute -top-14 -right-14 h-52 w-52 rounded-full bg-brand-bone/40 blur-3xl" aria-hidden />
            <div className="relative max-w-3xl">
              <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">A gentle first step</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">Not Ready for Group Work?</h3>
              <div className="mt-6 space-y-4 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
                <p>Many people feel uncertain about joining a therapeutic group. This is completely understandable.</p>
                <p>Individual therapy can provide a gentle starting point — helping you develop safety, confidence and therapeutic relationship before considering a group programme if and when it feels appropriate.</p>
                <p className="font-serif italic text-lg text-brand-brown-light">
                  There is no pressure to follow a particular pathway. Recovery unfolds differently for everyone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WORKING ALONGSIDE EXISTING CARE */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Collaborative care</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">Working Alongside Existing Care</h2>
            <p className="mt-5 font-sans text-base text-brand-brown-light/95 max-w-2xl mx-auto leading-relaxed">
              We are happy to work alongside:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {ALONGSIDE.map((line) => (
              <div key={line} className="bg-brand-bone rounded-2xl px-5 py-4 font-sans text-sm text-brand-brown-light/95 border border-brand-brown/5 text-center">
                {line}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center font-sans text-sm text-brand-brown-muted italic max-w-2xl mx-auto leading-relaxed">
            Where appropriate and with your consent, collaborative working can provide a more integrated and supportive approach to recovery.
          </p>
        </div>
      </section>

      {/* CLOSING INVITATION */}
      <section className="py-20 md:py-24 bg-brand-bg-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="divider-leaf mb-8" />
          <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">
            Wherever you are on your journey.
          </h2>
          <div className="mt-6 space-y-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            <p>Whether you are looking for individual psychotherapy, support for your family, guidance as a parent or couples therapy, we would be honoured to walk alongside you.</p>
            <p>Healing does not always begin in the same place. What matters most is taking the next step that feels right for you.</p>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="https://www.drcharlottebamber.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="therapy-cta-charlotte"
              className="inline-flex items-center gap-2 font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3 hover:bg-brand-brown-light transition-colors duration-500"
            >
              Book with Dr Charlotte Bamber <ExternalLink size={14} strokeWidth={1.6} />
            </a>
            <a
              href="https://therapeuticpaths.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="therapy-cta-jaquelynn"
              className="inline-flex items-center gap-2 font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-7 py-3 hover:bg-brand-brown-light transition-colors duration-500"
            >
              Book with Jaquelynn A. Taipe <ExternalLink size={14} strokeWidth={1.6} />
            </a>
          </div>

          <p className="mt-10 font-serif italic text-xl text-brand-gold tracking-wide">
            {CLOSING_STATEMENT.strapline}
          </p>

          <p className="mt-10 font-sans text-sm text-brand-brown-muted italic max-w-2xl mx-auto leading-relaxed">
            At Nurture the Self, there is no single route to recovery — whether through individual therapy, family work, couples therapy or our therapeutic pathways, we are committed to meeting each person with compassion, respect and the support that best fits their needs and stage of recovery.
          </p>

          <div className="mt-10">
            <Link
              to="/booking"
              data-testid="therapy-cta-programme"
              className="inline-flex font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-7 py-3 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              Prefer the group pathway? Book a Complimentary Conversation →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
